import type { ReactNode } from 'react';
import React from 'react';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import {
  faArrowLeft,
  faBell,
  faHome,
  faNewspaper,
  faSearch,
  faUser,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import {
  faBell as faBellSolid,
  faHome as faHomeSolid,
  faNewspaper as faNewspaperSolid,
  faSearch as faSearchSolid,
  faUser as faUserSolid,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { resetFilters, resetParams } from '@components/Explore/helpers';
import { setExploreType } from '@components/Explore/useSearchPageStore';
import { ActivityFeedState } from '@components/Feed/store';
import { Indicator } from '@components/Notifications/shared';
import { useUserNotificationStatus } from '@components/Notifications/useUserNotificationStatus';
import { SoundLogoV2 } from '@components/icons/SoundLogoV2';
import { AccessibleLink, type Pathname } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import { useNavActions } from '@hooks/useNavActions';
import { NAV_LINKS } from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { LeftNavType } from './helpers';
import { useLeftNavState } from './store';

const LabelLink = ({
  label,
  icon,
  path,
  onClick,
  isActive,
  showIndicator,
}: {
  label: string;
  icon: IconProp;
  path: string;
  isActive: boolean;
  showIndicator?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}) => {
  return (
    <AccessibleLink pathnameUri={webappUriPath(path)} onClick={onClick} className="!pl-0">
      <div className="-mx-2 flex items-center gap-4 rounded-full p-2 pl-4 transition-colors duration-300 hover:bg-base100">
        {icon && (
          <div className="relative flex w-7 justify-center">
            {showIndicator && <Indicator className="right-[1px] top-[-3px] h-2 w-2" />}
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center [&_svg]:h-[22px]">
              <FontAwesomeIcon
                icon={icon}
                className={clsx(isActive ? 'text-base900' : 'text-base600')}
              />
            </div>
          </div>
        )}
        <span
          className={clsx(
            'flex items-center py-1 font-title text-title-m',
            isActive ? 'font-semibold tracking-wide text-base900' : 'font-medium text-base600',
          )}
        >
          {label}
        </span>
      </div>
    </AccessibleLink>
  );
};

const LabelWithArrowLink = ({ pathnameUri }: { pathnameUri: Pathname }) => {
  return (
    <AccessibleLink
      pathnameUri={pathnameUri}
      className={clsx(
        'label-with-arrow flex h-8 items-center gap-2 rounded-full text-base800 transition-colors duration-300 ease-in-out',
        'w-[calc(100%-8px)] justify-center !pl-0 hover:bg-base200 lt:w-[calc(100%-24px)] lt:justify-start',
      )}
    >
      <div className="flex h-8 w-8 items-center justify-center">
        <FontAwesomeIcon icon={faArrowLeft} className="text-[20px] lt:text-[16px]" />
      </div>
      <span className="hidden font-title text-title-m font-medium text-base800 lt:inline">
        Back
      </span>
    </AccessibleLink>
  );
};

const IconLink = ({
  icon,
  path,
  showIndicator,
  isActive,
  onClick,
}: {
  icon: IconProp;
  path: string;
  isActive: boolean;
  showIndicator?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}) => {
  return (
    <AccessibleLink
      pathnameUri={webappUriPath(path)}
      onClick={onClick}
      className="relative flex items-center justify-center"
    >
      {showIndicator && <Indicator className="right-[1px] top-[-3px] h-2 w-2" />}
      <FontAwesomeIcon
        size="xl"
        icon={icon}
        className={clsx(isActive ? 'text-base900' : 'text-base600')}
      />
    </AccessibleLink>
  );
};

export const HeaderV2 = ({
  leftNavType = LeftNavType.DEFAULT,
}: {
  leftNavType?: keyof typeof LeftNavType;
}) => {
  const { previousPath } = useLeftNavState();
  const { authUser } = useAuth();
  const { hasUnreadNotifications } = useUserNotificationStatus();
  const {
    onNotificationsClick,
    onProfileClick,
    isExploreActive,
    isFeedActive,
    isHomepageActive,
    isNotificationActive,
    isProfileActive,
    isSearchActive,
  } = useNavActions();

  const userUrl = webappUriPath(authUser?.webappUri ?? '');

  let content: ReactNode;
  switch (leftNavType) {
    case LeftNavType.DEFAULT:
      content = (
        <>
          <div className="expanded-header hidden w-full flex-col gap-3 lt:flex">
            <LabelLink
              label="Home"
              path={NAV_LINKS.Home}
              isActive={isHomepageActive}
              icon={isHomepageActive ? faHomeSolid : faHome}
            />
            <LabelLink
              label="Feed"
              path={NAV_LINKS.Feed}
              isActive={isFeedActive}
              onClick={() => (ActivityFeedState.scrollIndex = 0)}
              icon={isFeedActive ? faNewspaperSolid : faNewspaper}
            />
            <LabelLink
              label="Explore"
              path={NAV_LINKS.Explore}
              isActive={isExploreActive}
              onClick={() => {
                resetParams();
                resetFilters();
                setExploreType({ type: null });
              }}
              icon={isExploreActive ? faSearchSolid : faSearch}
            />
            <LabelLink
              label="Notifications"
              path={NAV_LINKS.NotificationPage}
              isActive={isNotificationActive}
              icon={isNotificationActive ? faBellSolid : faBell}
              showIndicator={hasUnreadNotifications}
              onClick={onNotificationsClick}
            />
            <LabelLink
              label="Profile"
              path={authUser ? webappUriPath(authUser?.webappUri) : ''}
              isActive={isProfileActive}
              icon={isProfileActive ? faUserSolid : faUser}
              onClick={onProfileClick}
            />
          </div>
          <div className="collapsed-header flex flex-col gap-8 lt:hidden">
            <IconLink
              path={NAV_LINKS.Home}
              icon={isHomepageActive ? faHomeSolid : faHome}
              isActive={isHomepageActive}
            />
            <IconLink
              path={NAV_LINKS.Feed}
              icon={isFeedActive ? faNewspaperSolid : faNewspaper}
              onClick={() => (ActivityFeedState.scrollIndex = 0)}
              isActive={isFeedActive}
            />
            <IconLink
              path={NAV_LINKS.Explore}
              icon={isSearchActive ? faSearchSolid : faSearch}
              isActive={isSearchActive}
            />
            <IconLink
              path={NAV_LINKS.NotificationPage}
              icon={isNotificationActive ? faBellSolid : faBell}
              showIndicator={hasUnreadNotifications}
              onClick={onNotificationsClick}
              isActive={isNotificationActive}
            />
            <IconLink
              path={userUrl}
              icon={isProfileActive ? faUserSolid : faUser}
              onClick={onProfileClick}
              isActive={isProfileActive}
            />
          </div>
        </>
      );
      break;
    case LeftNavType.DASHBOARD:
      content = <LabelWithArrowLink pathnameUri={webappUriPath(previousPath)} />;
      break;
    case LeftNavType.UPLOAD:
    case LeftNavType.EDIT_UPLOAD:
      content = <LabelWithArrowLink pathnameUri={NAV_LINKS.Releases} />;
      break;
    default:
      return null;
  }

  return (
    <div
      className={clsx(
        'flex cursor-pointer flex-col gap-6 px-3 py-4 lt:px-4',
        leftNavType === LeftNavType.UPLOAD && 'lt:pb-1',
      )}
    >
      <AccessibleLink pathnameUri="/" className="flex items-center gap-2">
        <SoundLogoV2 color="black" />
      </AccessibleLink>
      <div className="flex flex-col items-center gap-1.5 lt:items-start">{content}</div>
    </div>
  );
};
